import React, { FC, ReactNode } from 'react';
import cx from 'classnames';

type Props = {
  className?: string;
};

const Part: FC<{ children: ReactNode }> = ({ children }) => (
  <h3 className="mt3 mb2" style={{ textAlign: 'center', fontWeight: 'bold' }}>
    {children}
  </h3>
);

const Section: FC<{ children: ReactNode }> = ({ children }) => (
  <h4 className="mt2 mb1" style={{ fontWeight: 'bold' }}>
    {children}
  </h4>
);

const Sub: FC<{ children: ReactNode }> = ({ children }) => (
  <h5 className="mt1 mb_5" style={{ fontWeight: 'bold' }}>
    {children}
  </h5>
);

const ContractTermsAndConditions: FC<Props> = ({ className }) => (
  <div className={cx('ContractTermsAndConditions akkurat', className)}>
    <div style={{ textAlign: 'center' }} className="mb2">
      <h2 className="mb_5" style={{ fontWeight: 'bold' }}>
        Light Phone
      </h2>
      <p className="mb_5" style={{ fontWeight: 'bold' }}>
        WIRELESS SERVICE AND PHONE PAYMENT AGREEMENT
      </p>
      <p>
        Unlimited Talk &amp; Text + Data Plan — Light Phone III Phone/Plan
        Bundle
      </p>
    </div>

    <p className="mb1">
      This Wireless Service and Phone Payment Agreement (this
      &ldquo;Agreement&rdquo;) is between the person activating, using, or paying
      for our Services (&ldquo;Customer,&rdquo; &ldquo;you,&rdquo; or
      &ldquo;your&rdquo;) and The Light Phone Inc.
    </p>
    <p className="mb1">
      This Agreement consists of two distinct components that operate in
      parallel: (1) a month-to-month Wireless Service Plan, which may be
      cancelled at any time without penalty; and (2) a Phone Payment Agreement
      (&ldquo;PPA&rdquo;) under which Light Phone provides you with a Light Phone
      III at no upfront cost for remaining on our service for a minimum of 24
      months. These two components are described separately below. Cancelling
      your Service Plan at any time — for any reason — does not extinguish your
      PPA obligations. If you cancel your Service Plan before the 24 months are
      up, the full remaining phone balance plus a $99.00 Service Cancellation
      Fee become immediately due and payable. See Part Two, Section 3.3 for the
      full payoff schedule.
    </p>
    <p className="mb1">
      <strong>
        BY ACTIVATING SERVICE OR ACCEPTING DELIVERY OF THE PHONE, YOU ACCEPT ALL
        TERMS OF THIS AGREEMENT. THIS AGREEMENT REQUIRES BINDING INDIVIDUAL
        ARBITRATION TO RESOLVE DISPUTES AND LIMITS THE REMEDIES AVAILABLE TO
        YOU.
      </strong>
    </p>

    <Part>PART ONE: WIRELESS SERVICE PLAN</Part>

    <Section>1. YOUR SERVICE PLAN</Section>
    <Sub>1.1 Plan Description</Sub>
    <p className="mb1">
      You may choose between two plans: Light Phone Unlimited Talk &amp; Text +
      1GB Data Plan at $59.00 per month or Light Phone Unlimited Talk &amp; Text
      + Unlimited Data Plan at $89 per month (&ldquo;Monthly Total
      Charge&rdquo;). This all-inclusive monthly charge covers both your
      wireless Service and your Phone Payment (described in Part Two) and
      includes unlimited domestic voice calls, unlimited domestic SMS and MMS
      messaging, and the specified amount of high-speed wireless data per
      billing cycle. Your Monthly Total Charge is the only recurring monthly
      amount you will be billed for this bundle, exclusive of applicable taxes,
      fees, and any separately purchased add-ons.
    </p>
    <Sub>1.2 Data Speed Management</Sub>
    <p className="mb1">
      If your high-speed allotment is consumed in a billing cycle, data speeds
      will be reduced to a maximum of 128 Kbps for the remainder of that cycle
      at no additional charge. High-speed access restores at the start of the
      next billing cycle. Unused data does not roll over.
    </p>

    <Section>2. SERVICE BILLING AND PAYMENT</Section>
    <Sub>2.1 Monthly Service Charges</Sub>
    <p className="mb1">
      Your Monthly Total Charge is billed in advance each billing cycle
      beginning on your Account Activation date. This charge covers both your
      wireless Service and your Phone Payment as a single all-in monthly
      obligation. Additional charges that may appear on your bill include taxes
      and governmental fees.
    </p>
    <Sub>2.2 AutoPay Enrollment and Authorization</Sub>
    <p className="mb1">
      Enrollment in AutoPay is required and must be completed within thirty (30)
      days of Phone shipment. By enrolling, you authorize Light Phone to
      automatically charge your Payment Method for: (a) your Monthly Total
      Charge each billing cycle; (c) the Phone Payoff Balance and $99.00 Service
      Cancellation Fee upon early Service cancellation; (d) any late fees,
      returned payment fees, or collection costs; (e) the Phone Retail Value
      ($699.00) for a non-returned Phone; and (f) any other amounts then due.
      This authorization survives Service termination until all amounts are paid
      in full. You agree to maintain a valid Payment Method on file at all
      times.
    </p>
    <Sub>2.3 Late Payments</Sub>
    <p className="mb1">
      Payments not received by the due date are late. Light Phone may assess a
      late fee of $5.00 per billing cycle, or the maximum permitted by
      applicable state law. Late fees are liquidated damages and not a penalty.
    </p>
    <Sub>2.4 Returned Payments</Sub>
    <p className="mb1">
      Light Phone may charge up to $30.00 (subject to applicable law) for any
      returned payment. Light Phone may require guaranteed payment forms
      following a returned payment.
    </p>
    <Sub>2.5 Billing Disputes</Sub>
    <p className="mb1">
      <strong>
        If you dispute any charges, contact Light Phone within sixty (60) days
        of the bill date by emailing support@lightphone.com. Failure to dispute
        within sixty (60) days waives your right to dispute those charges.
      </strong>
    </p>
    <Sub>2.6 Chargebacks</Sub>
    <p className="mb1">
      You agree not to initiate a chargeback with respect to any charge
      authorized under this Agreement except for genuinely unauthorized
      transactions resulting from fraud or identity theft. An improper
      chargeback constitutes a material breach. Upon receiving chargeback
      notice, Light Phone will provide ten (10) days to reverse it. If
      unreversed, Light Phone may suspend or terminate Service, accelerate the
      full Phone Payoff Balance and Service Cancellation Fee, and pursue all
      legal remedies including recovery of attorneys&rsquo; fees incurred
      contesting the chargeback.
    </p>
    <Sub>2.7 Suspension vs. Termination</Sub>
    <p className="mb1">
      Light Phone may suspend rather than terminate your Service for non-payment
      or cause. During suspension: (a) your Monthly Total Charge continues to
      accrue; (b) you retain your number for up to ninety (90) days before
      reclamation; and (c) you have no access to Services. Service restores upon
      payment of all past-due amounts plus any reactivation fee.
    </p>
    <Sub>2.8 Non-Payment as Deemed Termination</Sub>
    <p className="mb1">
      If any amount remains unpaid for thirty (30) or more consecutive days
      after the due date, Light Phone may deem your Service Plan terminated
      without further notice. The Phone Payoff Balance and Service Cancellation
      Fee become immediately due and Light Phone is authorized to charge them to
      the Payment Method on file.
    </p>
    <Sub>2.9 Credits After Cancellation</Sub>
    <p className="mb1">
      Credit balances over $25.00 will be returned within ninety (90) days of
      Service cancellation. Balances of $25.00 or less are returned only upon
      written request. Light Phone may apply credits to outstanding balances
      before issuing a refund.
    </p>
    <Sub>2.10 Number Portability</Sub>
    <p className="mb1">
      A request to port your number constitutes cancellation of your Service
      Plan. Your Phone Payoff Balance and Service Cancellation Fee become
      immediately due upon port-out.
    </p>
    <Sub>2.11 Account Transfer Upon Death or Incapacity</Sub>
    <p className="mb1">
      In the event of the death or legal incapacitation of the account holder,
      an authorized estate representative or legal guardian may request either:
      (a) transfer of the Service Plan and PPA to an eligible family member,
      subject to creditworthiness review; or (b) cancellation of the Service
      Plan with waiver of the Service Cancellation Fee (Phone Payoff Balance, if
      any, remains due). Documentation required may include a death certificate,
      letters testamentary, or guardianship order. Light Phone will process
      requests within thirty (30) days of receiving complete documentation.
    </p>
    <Sub>2.12 Post-PPA Service Continuation and Renewal</Sub>
    <p className="mb1">
      After your PPA Phone Balance is paid in full, your Service Plan continues
      on a month-to-month basis at the then-prevailing rate with no further
      phone obligations and no Service Cancellation Fee. Light Phone makes no
      commitment regarding the rate, plan, or terms applicable after the PPA is
      satisfied. Light Phone may, at its sole discretion, offer renewal or
      upgrade options, including new phone bundle agreements, subject to separate
      terms at that time.
    </p>

    <Part>PART TWO: PHONE PAYMENT AGREEMENT (PPA)</Part>

    <Section>3. PHONE PAYMENT AGREEMENT</Section>
    <Sub>3.1 Phone and Retail Value</Sub>
    <p className="mb1">
      Light Phone will provide you with one (1) Light Phone III phone
      (&ldquo;Phone&rdquo;) at no upfront cost. The Light Phone III has a current
      retail value of $699.00 (&ldquo;Phone Retail Value&rdquo;). Under this
      PPA, you pay for the Phone through monthly installments rather than a lump
      sum at purchase.
    </p>
    <Sub>3.2 Amount Due Upon Early Service Cancellation</Sub>
    <p className="mb1">
      If you cancel your Service Plan at any time while a Phone Balance remains
      outstanding, the following amounts become immediately due: a $99 early
      termination charge, and the retail cost of the phone, less a $35 credit
      for each month your contract paid to the point of cancellation.
    </p>
    <p className="mb1">
      Light Phone is authorized to charge the Total Amount Due to the Payment
      Method on file at the time of cancellation without further notice.
    </p>
    <Sub>3.3 Phone Shipment and Activation Deadline</Sub>
    <p className="mb1">
      Your Phone will be shipped to your enrollment address. You must activate
      your Service and enroll in AutoPay within thirty (30) days of Phone
      shipment (&ldquo;Activation Deadline&rdquo;). If you do not activate by the
      Activation Deadline, Light Phone may cancel your order and request Phone
      return. Failure to return the Phone within fourteen (14) days of
      cancellation notice will result in a charge of the full Phone Retail Value
      ($699.00).
    </p>
    <Sub>3.4 Phone Inspection</Sub>
    <p className="mb1">
      Inspect your Phone promptly upon receipt. If you believe the Phone has a
      manufacturer&rsquo;s defect, contact Light Phone, Inc. at
      support@thelightphone.com within five (5) business days of delivery.
    </p>
    <Sub>3.5 Lost or Stolen Phones — Service Options</Sub>
    <p className="mb1">
      Loss or theft of the Phone does not automatically suspend or terminate
      your PPA obligations. Following a reported theft you have three options:
    </p>
    <ul className="mb1">
      <li>
        Continue Service on any compatible Phone on the Light Service Plan. You
        are not required to use a Light Phone III for the remainder of the PPA
        term. Monthly Service and Phone Payment obligations continue.
      </li>
      <li>
        Obtain a Replacement via Light Protect Plan. File a claim with Akko.
        Your PPA continues on the replacement Phone on the same terms, accepted
        in good faith without a re-lock requirement.
      </li>
      <li>Cancel Service and pay cancellation fees.</li>
    </ul>
    <Sub>3.6 Risk-Free Trial Phone Return</Sub>
    <p className="mb1">
      There is no risk-free trial period applicable to this Phone Payment
      Agreement. Your PPA obligations commence upon Account Creation and are not
      subject to any trial cancellation right by Light Phone, Inc.&rsquo;s
      warranty against manufacturer&apos;s defect is in effect. Any other return
      or cancellation of this PPA is subject to Section 3.2
    </p>
    <Sub>3.7 Phone Lock and Unlock</Sub>
    <p className="mb1">
      The Phone is SIM-locked to Light Phone for the PPA term. Light Phone will
      provide an unlock code after: (a) all 24 Monthly Phone Payments are
      complete; and (b) your account is in good standing. Unlocking before PPA
      completion does not discharge your PPA obligations or Phone Balance.
    </p>
    <Sub>3.8 Light Protect Plan (Optional Phone Insurance)</Sub>
    <p className="mb1">
      Light Phone, Inc. offers an optional Light Protect Plan via Akko covering
      accidental damage, loss, and theft. Current key terms: $9.99/month; $49.00
      repair deductible; $99.00 replacement deductible; IMEI must be registered
      within seven (7) days of Phone receipt; software issues excluded. To
      enroll: support.thelightphone.com/hc/en-us/articles/36096754325524-Light-Protect-Plan.
    </p>

    <Part>PART THREE: GENERAL TERMS</Part>

    <Section>4. USE OF SERVICE</Section>
    <Sub>4.1 Permitted Use</Sub>
    <p className="mb1">
      Service is for your personal, non-commercial use only. You may not resell
      Service to any third party.
    </p>
    <Sub>4.2 Prohibited Uses</Sub>
    <p className="mb1">
      Prohibited uses include reselling data access; unauthorized bulk
      messaging; transmitting unlawful content; degrading network performance;
      and tampering with Light Phone&rsquo;s network or billing systems.
    </p>
    <Sub>4.3 Network Management</Sub>
    <p className="mb1">
      Light Phone employs reasonable network management practices to ensure
      equitable access. Light Phone may deny, disconnect, or modify Service for
      users whose usage adversely impacts network integrity.
    </p>

    <Section>5. PHONE RESPONSIBILITY</Section>
    <Sub>5.1 Care and SIM</Sub>
    <p className="mb1">
      You are responsible for all Phones and SIM cards on your account. You
      agree not to alter, bypass, or reverse-engineer your SIM card. Light Phone
      may remotely update your Phone&rsquo;s system settings. Where available on
      compatible, eligible unlocked phones and supported plans, Light Phone may
      offer eSIM activation. eSIM availability is subject to Light Phone&rsquo;s
      sole discretion and may change at any time. Customers with compatible
      phones may switch between eSIM and a physical SIM card. One activated eSIM
      per phone is permitted.
    </p>

    <Section>6. VOICE, DATA, AND MESSAGING</Section>
    <Sub>6.1 Voice</Sub>
    <p className="mb1">
      Unlimited domestic calling within the United States is included.
      International calls incur additional charges. Light Phone does not
      guarantee call quality or continuity.
    </p>
    <Sub>6.2 Messaging</Sub>
    <p className="mb1">
      Unlimited domestic SMS and MMS is included. International messages incur
      additional charges. SMS limited to 160 characters per segment; MMS to 1MB.
      Light Phone does not guarantee delivery or timeliness.
    </p>
    <Sub>6.3 Data</Sub>
    <p className="mb1">
      Provision of high-speed data per billing cycle as described in Section
      1.3.
    </p>
    <Sub>6.4 International Roaming</Sub>
    <p className="mb1">
      Your Plan is for use within the United States. Light Phone does not offer
      international roaming.
    </p>
    <Sub>6.5 911 Service</Sub>
    <p className="mb1">
      Light Phone supports 911 emergency calling but cannot guarantee connection
      or that emergency responders will locate you. Maintain a current primary
      service address for Wi-Fi 911 routing.
    </p>

    <Section>7. CHANGES TO THIS AGREEMENT</Section>
    <p className="mb1">
      Light Phone may amend this Agreement at any time with advance notice of
      material changes via bill or email. If a material change negatively
      impacts your Service, you may cancel your Service Plan within thirty (30)
      days without a Service Cancellation Fee (Phone Payoff Balance, if any,
      remains due). Continued use after 30 days constitutes acceptance. Changes
      to governmental fees, surcharges, or taxes do not require advance notice.
    </p>

    <Section>8. ACCOUNT ACCESS AND SECURITY</Section>
    <p className="mb1">
      You may register for an account at thelightphone.com. Maintain the
      security of your credentials and notify Light Phone immediately if
      compromised. You may designate authorized users aged 18 or older and are
      responsible for all actions they take. Light Phone is not liable for
      losses from unauthorized use of your account or credentials.
    </p>

    <Section>9. FORCE MAJEURE</Section>
    <p className="mb1">
      Light Phone&rsquo;s service obligations are excused during Force Majeure
      Events beyond its reasonable control, including acts of God, natural
      disasters, pandemic, government action, network failure, or other
      causes outside Light Phone&rsquo;s control. During a Force Majeure Event:
      (a) service obligations are suspended without liability; (b) the PPA term
      and Phone Balance continue without tolling; and (c) Service and Phone
      charges continue to accrue. If a Force Majeure Event prevents Service for
      more than thirty (30) consecutive days, you may cancel your Service Plan
      without a Service Cancellation Fee (Phone Payoff Balance remains due) upon
      written notice during the event.
    </p>

    <Section>10. CREDIT CHECK AND ADVERSE ACTION</Section>
    <p className="mb1">
      Light Phone may obtain your credit report in connection with your
      application and on an ongoing basis. Light Phone may deny Service, require
      a deposit, or limit Services based on credit review. If Light Phone takes
      adverse action based on your credit report, it will provide required FCRA
      and ECOA notices. Light Phone may report account information, including
      delinquencies, to consumer reporting agencies.
    </p>

    <Section>
      11. DISCLAIMER OF WARRANTIES AND LIMITATION OF LIABILITY
    </Section>
    <Sub>11.1 Disclaimer</Sub>
    <p className="mb1">
      <strong>
        Services are provided &ldquo;as-is&rdquo; and &ldquo;as available.&rdquo;
      </strong>
    </p>
    <Sub>11.2 Limitation of Liability</Sub>
    <p className="mb1">
      <strong>
        Light Phone is not liable for any indirect, special, punitive,
        incidental, or consequential damages from use of or inability to use
        Services or the Phone, including damages from Phone defects or Light
        Phone&rsquo;s warranty decisions. Light Phone&rsquo;s total liability
        shall not exceed charges you paid for Service in the three (3) months
        preceding the claim.
      </strong>
    </p>
    <p className="mb1">
      If Service is interrupted for twenty-four (24) or more continuous hours
      due to a cause within Light Phone&rsquo;s control, you may request a
      prorated Service credit not to exceed one month&rsquo;s Service fee.
    </p>
    <Sub>11.3 Indemnification</Sub>
    <p className="mb1">
      You agree to indemnify and hold Light Phone, its officers, directors,
      employees, and affiliates harmless from any claims, losses, or damages,
      including attorneys&rsquo; fees, arising from your use of Service,
      violation of this Agreement, or any third party&rsquo;s use of your Phone
      or account.
    </p>

    <Section>12. DISPUTE RESOLUTION BY ARBITRATION</Section>
    <p className="mb1">
      <strong>
        PLEASE READ CAREFULLY. THIS SECTION AFFECTS YOUR LEGAL RIGHTS INCLUDING
        YOUR RIGHT TO FILE A LAWSUIT IN COURT.
      </strong>
    </p>
    <Sub>12.1 Informal Resolution</Sub>
    <p className="mb1">Contact support@thelightphone.com.</p>
    <Sub>12.2 Small Claims</Sub>
    <p className="mb1">
      Either party may bring an individual claim in small claims court if within
      that court&rsquo;s jurisdiction.
    </p>
    <Sub>12.3 Limitations Period</Sub>
    <p className="mb1">
      All claims must be filed within two (2) years of the event giving rise to
      the dispute or are permanently barred.
    </p>

    <Section>13. PRIVACY, DATA USE, AND COMMUNICATIONS CONSENT</Section>
    <Sub>13.1 Privacy Policy</Sub>
    <p className="mb1">
      Light Phone collects and uses your information consistent with our Privacy
      Policy at https://www.thelightphone.com/privacy-policy. By using our
      Services, you consent to such collection and use.
    </p>
    <Sub>13.2 Operational Communications</Sub>
    <p className="mb1">
      You agree Light Phone and its agents may contact you for operational and
      transactional purposes — billing, payment, Service notices, account
      security — via any channel including automated systems and pre-recorded
      voice. These communications are a condition of Service and cannot be opted
      out of while your account is active.
    </p>
    <Sub>13.3 Marketing Communications</Sub>
    <p className="mb1">
      Marketing communications are voluntary and not a condition of Service. Opt
      out at any time by replying &ldquo;STOP&rdquo; to any marketing text,
      clicking unsubscribe in any marketing email, or contacting
      support@thelightphone.com. Opting out does not affect operational
      communications.
    </p>
    <Sub>13.4 Data Breach Notification</Sub>
    <p className="mb1">
      In the event of a breach involving your personal information, Light Phone
      will notify you in accordance with applicable state data breach
      notification laws, describing the nature, affected categories, and Light
      Phone&rsquo;s response.
    </p>
    <Sub>13.5 Children&rsquo;s Privacy</Sub>
    <p className="mb1">
      Light Phone&rsquo;s Services are not directed to children under 13. Light
      Phone will not knowingly collect personal information from children under
      13 without verifiable parental consent, consistent with COPPA.
    </p>
    <Sub>13.6 Customer Proprietary Network Information (CPNI)</Sub>
    <p className="mb1">
      Light Phone may collect Customer Proprietary Network Information
      (&ldquo;CPNI&rdquo;) — information about the telecommunications services
      you purchase, how you use them, and related billing information — as part
      of providing your Service. Light Phone uses CPNI to provide and improve
      your Service, for training and quality assurance, and to offer you
      additional services of the type you already purchase. Light Phone may also
      use your CPNI to offer you products, packages, discounts, and promotions
      from its affiliates. Light Phone does not sell CPNI to unaffiliated third
      parties and does not disclose CPNI outside of Light Phone, its affiliates,
      and their respective agents without your consent, except as required by
      law. If you wish to restrict Light Phone&rsquo;s use of your CPNI for
      marketing purposes, contact Light at support@thelightphone.com.
    </p>

    <Section>14. GENERAL TERMS</Section>
    <Sub>14.1 Electronic Signatures</Sub>
    <p className="mb1">
      Activation of Service through any Light Phone-authorized channel
      constitutes your electronic signature and binding acceptance of all terms
      of this Agreement, including both the Service Plan and PPA, with the same
      legal force as a handwritten signature under the E-Sign Act, 15 U.S.C.
      § 7001 et seq. Light Phone will provide a copy of this Agreement
      electronically upon request.
    </p>
    <Sub>14.2 Notices</Sub>
    <p className="mb1">
      Notices from Light Phone are effective when sent to your email of record.
    </p>
    <Sub>14.3 Number Ownership</Sub>
    <p className="mb1">
      You have no ownership interest in your phone number. Light Phone may change
      your number upon notice. Following Service termination, your number will
      be held for up to sixty (60) days before reclamation.
    </p>
    <Sub>14.4 Assignment</Sub>
    <p className="mb1">
      Light Phone may assign this Agreement without notice. You may not assign
      this Agreement without Light Phone&rsquo;s prior written consent.
    </p>
    <Sub>14.5 Governing Law</Sub>
    <p className="mb1">
      This Agreement is governed by the laws of the State of Delaware, without
      regard to conflict of law principles, except to the extent federal law
      governs. Nothing in this Agreement limits any rights you may have under
      the mandatory consumer protection laws of your state of residence.
    </p>
    <Sub>14.6 Entire Agreement and Conflicts</Sub>
    <p className="mb1">
      This Agreement, constitutes the entire agreement between you and Light
      Phone regarding Services and the Phone.
    </p>
    <Sub>14.7 Severability and No Waiver</Sub>
    <p className="mb1">
      If any provision is unenforceable, the remaining provisions remain in
      force. Failure to enforce any provision does not waive Light Phone&rsquo;s
      right to enforce it subsequently.
    </p>
    <Sub>14.8 Wireless Emergency Alerts</Sub>
    <p className="mb1">
      Light Phone delivers wireless emergency alerts at no additional charge to
      capable phones.
    </p>
    <Sub>14.9 Survival</Sub>
    <p className="mb1">
      Sections 2.5, 2.8, 3.3, 3.6, 11, 12, 13, and 14 survive termination of
      this Agreement.
    </p>

    <Section>15. STATE-SPECIFIC PROVISIONS</Section>
    <Sub>15.1 California — Unauthorized Charges</Sub>
    <p className="mb1">
      You are not liable for unauthorized charges. Light Phone will investigate
      disputed charges and advise you within thirty (30) days. You may file a
      complaint with the CPUC at cpuc.ca.gov/complaints or 800-649-7570.
      Disputed charges need not be paid during a CPUC investigation. California
      customers retain all rights under California consumer protection law
      notwithstanding any choice-of-law provision in this Agreement.
    </p>
    <Sub>15.2 State Privacy Rights</Sub>
    <p className="mb1">
      Residents of California and other states with comprehensive privacy laws
      may have additional rights regarding personal information.
    </p>

    <Section>CUSTOMER ACKNOWLEDGMENT</Section>
    <p className="mb1">
      By activating Service or accepting delivery of the Phone, I acknowledge
      that I have read, understood, and agreed to all terms of this Agreement,
      including: the month-to-month Service Plan (Part One); the Phone Payment
      Agreement, Phone Balance schedule, and $99.00 Service Cancellation Fee
      (Part Two, Section 3.3); the Theft Accommodation terms and one-time limit
      (Section 3.6); the absence of any risk-free trial or satisfaction
      guarantee (Section 2.10); the AutoPay authorization (Section 2.2); the
      binding arbitration provision (Section 12); and the Light Phone, Inc.
      warranty pass-through (Section 3.10). I understand that my Phone Payment
      obligations under Part Two are independent of my Service Plan and survive
      Service cancellation until paid in full. My electronic acceptance through
      Light Phone&rsquo;s activation flow constitutes a valid electronic
      signature under the E-Sign Act.
    </p>
  </div>
);

export default ContractTermsAndConditions;
